import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

const ACCENT = '#2B7FFF';

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function formatDate(d) {
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function fmtDate(d) {
  return d.toISOString().split('T')[0];
}

const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
  late_cancelled: 'Late Cancel',
  declined: 'Declined',
  no_show: 'No Show',
};

const STATUS_COLORS = {
  pending: '#f59e0b',
  confirmed: '#3b82f6',
  completed: '#10b981',
  cancelled: '#6b7280',
  late_cancelled: '#ef4444',
  declined: '#ef4444',
  no_show: '#ef4444',
};

function RescheduleModal({ appt, onClose, onSuccess }) {
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(fmtDate(addDays(today, 1)));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(today, i + 1);
    return { date: fmtDate(d), day: d.toLocaleDateString('en-US', { weekday: 'short' }), num: d.getDate() };
  });

  const loadSlots = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setLoadingSlots(true);
    setSlots([]);
    // We need service_id — use appt.service_id if available or skip
    if (!appt.service_id) { setLoadingSlots(false); return; }
    api.get(`/availability?date=${date}&service_id=${appt.service_id}`)
      .then(r => setSlots(r.data))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  };

  React.useEffect(() => { loadSlots(selectedDate); }, []); // eslint-disable-line

  const handleSubmit = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    setError('');
    try {
      await api.put(`/appointments/${appt.id}/reschedule`, {
        new_date: selectedDate,
        new_time: selectedSlot.start_time,
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Reschedule failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
    }}>
      <div style={{
        background: '#0F1929', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px',
        padding: '28px 24px', maxWidth: '460px', width: '100%',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontWeight: 800, fontSize: '18px', textTransform: 'uppercase' }}>Reschedule</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#555', fontSize: '20px', cursor: 'pointer' }}>×</button>
        </div>

        <p style={{ color: '#888', fontSize: '13px', marginBottom: '18px' }}>
          Pick a new date and time for your <strong style={{ color: '#fff' }}>{appt.service_name}</strong>.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '16px' }}>
          {weekDays.map(({ date, day, num }) => {
            const isSelected = selectedDate === date;
            return (
              <button key={date} onClick={() => loadSlots(date)} style={{
                padding: '8px 3px', borderRadius: '6px', border: '1px solid',
                borderColor: isSelected ? ACCENT : '#333',
                background: isSelected ? 'rgba(43,127,255,0.1)' : '#050810',
                color: isSelected ? ACCENT : '#888', cursor: 'pointer', textAlign: 'center',
              }}>
                <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase' }}>{day}</div>
                <div style={{ fontSize: '14px', fontWeight: 800 }}>{num}</div>
              </button>
            );
          })}
        </div>

        {loadingSlots ? (
          <div style={{ textAlign: 'center', padding: '20px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : slots.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#555', padding: '16px', fontSize: '13px' }}>No available slots this day.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(85px, 1fr))', gap: '6px', marginBottom: '16px', maxHeight: '180px', overflowY: 'auto' }}>
            {slots.map(slot => {
              const isSelected = selectedSlot?.start_time === slot.start_time;
              return (
                <button key={slot.start_time} onClick={() => setSelectedSlot(slot)} style={{
                  padding: '9px', borderRadius: '6px', border: '1px solid',
                  borderColor: isSelected ? ACCENT : slot.is_after_hours ? 'rgba(43,127,255,0.25)' : '#333',
                  background: isSelected ? 'rgba(43,127,255,0.15)' : slot.is_after_hours ? 'rgba(43,127,255,0.04)' : '#050810',
                  color: isSelected ? ACCENT : slot.is_after_hours ? ACCENT : '#ccc',
                  cursor: 'pointer', fontWeight: 700, fontSize: '12px',
                }}>
                  {formatTime(slot.start_time)}
                </button>
              );
            })}
          </div>
        )}

        {error && (
          <div style={{ color: '#ef4444', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '14px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={handleSubmit} disabled={!selectedSlot || submitting} className="btn btn-primary" style={{ flex: 1, padding: '13px', fontSize: '14px' }}>
            {submitting ? 'Rescheduling...' : 'Confirm'}
          </button>
          <button onClick={onClose} className="btn btn-ghost" style={{ padding: '13px 18px' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function MyAppointment() {
  const [form, setForm] = useState({ email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!form.email && !form.phone) return setError('Enter your email or phone number.');
    setLoading(true);
    setError('');
    setData(null);
    try {
      const { data: result } = await api.post('/clients/lookup', {
        email: form.email || undefined,
        phone: form.phone || undefined,
      });
      setData(result);
    } catch (err) {
      setError(err.response?.data?.error || 'Lookup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this appointment?')) return;
    setCancellingId(id);
    try {
      await api.put(`/appointments/${id}/cancel`);
      setSuccessMsg('Appointment cancelled.');
      setData(prev => ({
        ...prev,
        appointments: prev.appointments.map(a =>
          a.id === id ? { ...a, status: 'cancelled' } : a
        ),
      }));
    } catch (err) {
      alert(err.response?.data?.error || 'Cancel failed.');
    } finally {
      setCancellingId(null);
    }
  };

  const handleRescheduleSuccess = (apptId) => {
    setRescheduleAppt(null);
    setSuccessMsg('Appointment rescheduled! A confirmation email has been sent.');
    // Refresh data
    api.post('/clients/lookup', { email: form.email || undefined, phone: form.phone || undefined })
      .then(r => setData(r.data))
      .catch(() => {});
  };

  const today = new Date().toISOString().split('T')[0];
  const upcoming = data?.appointments?.filter(a => a.appointment_date >= today && ['pending','confirmed'].includes(a.status)) || [];
  const past = data?.appointments?.filter(a => a.appointment_date < today || !['pending','confirmed'].includes(a.status)) || [];

  return (
    <div style={{ maxWidth: '580px', margin: '60px auto', padding: '24px' }}>
      {rescheduleAppt && (
        <RescheduleModal
          appt={rescheduleAppt}
          onClose={() => setRescheduleAppt(null)}
          onSuccess={() => handleRescheduleSuccess(rescheduleAppt.id)}
        />
      )}

      <div style={{ textAlign: 'center', marginBottom: '36px' }}>
        <div style={{ color: ACCENT, fontSize: '11px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '10px' }}>
          MANE
        </div>
        <h1 style={{ fontSize: '28px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '-0.01em', marginBottom: '8px' }}>
          My Appointments
        </h1>
        <p style={{ color: '#888', fontSize: '14px' }}>Look up your bookings by email or phone.</p>
      </div>

      {/* Lookup form */}
      {!data && (
        <form onSubmit={handleLookup} className="card" style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '14px' }}>
            <label className="label">Email</label>
            <input
              className="input"
              type="email"
              placeholder="your@email.com"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label className="label">Phone</label>
            <input
              className="input"
              type="tel"
              placeholder="(561) 555-0000"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            />
          </div>
          {error && (
            <div style={{ color: '#ef4444', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '14px' }}>
              {error}
            </div>
          )}
          <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '14px' }} disabled={loading}>
            {loading ? 'Looking up...' : 'Find My Appointments'}
          </button>
        </form>
      )}

      {data && (
        <>
          {successMsg && (
            <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '6px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#10b981' }}>
              {successMsg}
            </div>
          )}

          {/* Client info bar */}
          <div style={{ background: '#0F1929', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '16px 20px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '16px' }}>{data.client.name}</div>
              <div style={{ color: '#888', fontSize: '13px', marginTop: '2px' }}>{data.client.email || data.client.phone}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: ACCENT, fontWeight: 900, fontSize: '22px', lineHeight: 1 }}>{data.client.points || 0}</div>
              <div style={{ color: '#555', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {data.client.points >= 10 ? '$10 off earned!' : `Points (${10 - (data.client.points % 10)} to $10 off)`}
              </div>
            </div>
          </div>

          {/* Upcoming */}
          {upcoming.length > 0 && (
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', marginBottom: '12px' }}>
                Upcoming ({upcoming.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {upcoming.map(a => (
                  <div key={a.id} style={{
                    background: '#0F1929', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '16px',
                    borderLeft: `3px solid ${STATUS_COLORS[a.status] || '#2a2a2a'}`,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '15px' }}>{a.service_name}</div>
                        <div style={{ color: '#9ca3af', fontSize: '13px', marginTop: '2px' }}>
                          {formatDate(a.appointment_date)} · {formatTime(a.start_time)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: ACCENT, fontWeight: 800, fontSize: '17px' }}>${parseFloat(a.final_price || 0).toFixed(0)}</div>
                        <span style={{
                          fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                          background: `${STATUS_COLORS[a.status]}20`, color: STATUS_COLORS[a.status],
                          border: `1px solid ${STATUS_COLORS[a.status]}40`,
                        }}>
                          {STATUS_LABELS[a.status] || a.status}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-outline"
                        style={{ padding: '8px 14px', fontSize: '13px' }}
                        onClick={() => setRescheduleAppt(a)}
                      >
                        Reschedule
                      </button>
                      <button
                        className="btn btn-ghost"
                        style={{ padding: '8px 14px', fontSize: '13px', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}
                        disabled={cancellingId === a.id}
                        onClick={() => handleCancel(a.id)}
                      >
                        {cancellingId === a.id ? 'Cancelling...' : 'Cancel'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {upcoming.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px', background: '#0F1929', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', marginBottom: '24px' }}>
              
              <div style={{ color: '#888', marginBottom: '16px' }}>No upcoming appointments.</div>
              <Link to="/book" className="btn btn-primary" style={{ fontSize: '14px' }}>Book Now</Link>
            </div>
          )}

          {/* Past */}
          {past.length > 0 && (
            <div>
              <h3 style={{ fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', marginBottom: '12px' }}>
                History ({past.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {past.map(a => (
                  <div key={a.id} style={{
                    background: '#0B1120', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '8px', padding: '12px 16px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px',
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '14px' }}>{a.service_name}</div>
                      <div style={{ color: '#555', fontSize: '12px', marginTop: '2px' }}>{formatDate(a.appointment_date)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{
                        fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                        background: `${STATUS_COLORS[a.status] || '#6b7280'}20`,
                        color: STATUS_COLORS[a.status] || '#6b7280',
                        border: `1px solid ${STATUS_COLORS[a.status] || '#6b7280'}40`,
                      }}>
                        {STATUS_LABELS[a.status] || a.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: '28px' }}>
            <button
              onClick={() => { setData(null); setSuccessMsg(''); }}
              className="btn btn-ghost"
              style={{ fontSize: '13px', color: '#555' }}
            >
              Look up another account
            </button>
          </div>
        </>
      )}

      <div style={{ textAlign: 'center', marginTop: '32px' }}>
        <Link to="/" style={{ color: '#555', fontSize: '13px', textDecoration: 'none' }}>← Back to Home</Link>
      </div>
    </div>
  );
}
